import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { ShopifyAjaxClient } from "@redotech/shopify-client/ajax";
import { getAddToCartButton } from "./pdp-utils";

export async function addTnplProductToCart({
  autocheck,
  productForm,
  sellingPlanId,
}: {
  autocheck: WidgetAutocheck;
  productForm: HTMLElement;
  sellingPlanId: string;
}) {
  if (autocheck.tryFirst?.widget?.clickOriginalAddToCartButton) {
    await addTnplProductViaOriginalButton(productForm, sellingPlanId);
  } else {
    // This doesn't reliably trigger refreshes or open cart drawer, but it would be cleaner if we could make it work consistently with different themes.
    await addTnplProductViaApi(productForm, sellingPlanId);
    window.dispatchEvent(
      new CustomEvent("cart:added", { detail: { sellingPlanId } }),
    );
    window.dispatchEvent(new Event("cart:update"));
  }
}

export async function addTnplProductViaApi(
  productForm: HTMLElement,
  sellingPlanId: string,
): Promise<void> {
  const formData = new FormData(productForm as unknown as HTMLFormElement);
  const entries = Array.from(formData.entries());

  const variantId = entries.find(([key]) => key === "id")?.[1];
  const quantity = entries.find(([key]) => key === "quantity")?.[1] ?? 1;

  if (!variantId) {
    throw new Error("Product variant ID not found");
  }

  const properties: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (
      key.startsWith("properties[") &&
      key.endsWith("]") &&
      typeof value === "string"
    ) {
      const propKey = key.slice(11, -1);
      properties[propKey] = value;
    }
  }

  const ajaxClient = ShopifyAjaxClient.fromEnv();
  await ajaxClient.cartAdd({
    items: [
      {
        id: parseInt(variantId.toString(), 10),
        quantity: parseInt(quantity.toString(), 10),
        selling_plan: parseInt(sellingPlanId, 10),
        properties,
      },
    ],
  });
}

export async function addTnplProductViaOriginalButton(
  productForm: HTMLElement,
  sellingPlanId: string,
): Promise<void> {
  const submitButton = getAddToCartButton(productForm);

  const doesShopPaySellingPlanDisclosureExistBeforeClick =
    !!productForm.querySelector(
      ".shopify-payment-button #shopify-buyer-consent",
    );

  if (!submitButton) {
    throw new Error("Add to cart button not found");
  }

  const sellingPlanInput = document.createElement("input");
  sellingPlanInput.type = "hidden";
  sellingPlanInput.name = "selling_plan";
  sellingPlanInput.value = sellingPlanId;
  productForm.append(sellingPlanInput);

  // Shop Pay buttons add some disclosure text if there's a selling plan in the product form. Remove it so it doesn't create a weird flash of content.
  if (!doesShopPaySellingPlanDisclosureExistBeforeClick) {
    productForm
      .querySelector(".shopify-payment-button #shopify-buyer-consent")
      ?.remove();
  }

  submitButton.click();

  setTimeout(() => {
    sellingPlanInput.remove();
  }, 100);
}
